/**
 * PegasusJobPeek (BOARD-PLACE) — clicking a Pegasus-scheduled chip on the board
 * opens this compact detail, the way an ESC stop opens its dispatch detail (it
 * does NOT jump to the 360). Shows the job, lets you set the exact arrival time,
 * open the customer, or send it back to the queue. ESC is never touched.
 */
import { useNavigate } from "react-router-dom";
import { Clock, Phone, Inbox, UserRound, Sparkles, CalendarDays } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { useUnschedule } from "@/features/customer-360/scheduleDispatch";
import { useSetDispatchTime, type PendingTicket } from "./queue";

/**
 * `from` tunes the footer for where the peek opened. On the BOARD (default) the
 * dispatcher wants "Open customer"; from the CUSTOMER 360 that's useless (you're
 * already there) — instead jump to the board on this call's day, where the
 * Pegasus dispatch lives and is edited (there's no ESC-style standalone edit
 * page for Pegasus dispatches).
 */
export function PegasusJobPeek({ job, onClose, from = "board" }: { job: PendingTicket; onClose: () => void; from?: "board" | "customer" }) {
  const navigate = useNavigate();
  const unschedule = useUnschedule();
  const setTime = useSetDispatchTime();
  const urgent = job.urgency === "asap";

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      icon={<Sparkles className="h-4 w-4 text-brand-500" />}
      title={job.customer_name}
      subtitle={job.service_type_name ?? job.summary}
    >
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-1.5">
          {job.scheduled_date ? (
            <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
              {job.scheduled_date}
            </span>
          ) : (
            <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
              Not placed on a board yet
            </span>
          )}
          {job.scheduled_window && (
            <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
              {job.scheduled_window}
            </span>
          )}
          {urgent && (
            <span className="rounded-md bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">ASAP</span>
          )}
          <span className="rounded-md bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-600">
            Pegasus-scheduled
          </span>
        </div>

        <label className="block">
          <span className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <Clock className="h-3 w-3" /> Exact arrival time <span className="text-slate-400">(sequences it on the board)</span>
          </span>
          <input
            type="time"
            defaultValue={job.scheduled_time?.slice(0, 5) ?? ""}
            onBlur={(e) => setTime.mutate({ id: job.id, time: e.target.value || null })}
            className="w-36 rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </label>

        {job.city && <div className="text-xs text-slate-500">{[job.address, job.city].filter(Boolean).join(", ")}</div>}
        {job.call_ahead && (
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <Phone className="h-3.5 w-3.5 text-slate-400" /> {job.call_ahead_method === "text" ? "Text" : "Call"} on the way{job.call_ahead_phone ? ` · ${job.call_ahead_name ? `${job.call_ahead_name} ` : ""}${job.call_ahead_phone}` : ""}
          </div>
        )}
        {job.notes && <p className="whitespace-pre-wrap rounded-md bg-slate-50 px-3 py-2 text-xs leading-snug text-slate-600">{job.notes}</p>}

        <div className="flex items-center justify-between gap-2 border-t border-hairline pt-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { unschedule.mutate(job.id); onClose(); }}
            disabled={unschedule.isPending}
            title="Unassign + return to the Incoming queue"
          >
            <Inbox className="h-3.5 w-3.5" /> Back to queue
          </Button>
          {from === "customer" ? (
            <Button
              size="sm"
              onClick={() => navigate(job.scheduled_date ? `/board?date=${job.scheduled_date}` : "/board")}
              title="See and edit this call on the dispatch board"
            >
              <CalendarDays className="h-3.5 w-3.5" /> Show on board
            </Button>
          ) : (
            <Button size="sm" onClick={() => job.custno && navigate(`/customers/${job.custno}`)}>
              <UserRound className="h-3.5 w-3.5" /> Open customer
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
